import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ColumnDef,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { useFeatureFlags } from '@/modules/common/hooks/use-feature-flags';
import {
  OpsiumPolicy,
  OVERHEADS_OPSIUM_POLICIES,
  POLICY_CATEGORIES,
  POLICY_DEPENDENCIES,
  POLICY_DEPENDENTS,
  TIME_OFF_OPSIUM_POLICIES,
} from '@/modules/common/types/opsium-policy';
import { DeleteUserGroupModal } from '@/modules/settings/components/permissions/delete-user-group-modal';
import { PermissionsTable } from '@/modules/settings/components/permissions/permissions-table';
import { UserGroupFormModal } from '@/modules/settings/components/permissions/user-group-form-modal';
import {
  getPermissionsColumns,
  PERMISSION_POLICY_COLUMN_ID,
} from '@/modules/settings/constants/permissions-table-columns';
import { usePermissions } from '@/modules/settings/hooks/permissions/use-permissions';
import { useUpdatePermissions } from '@/modules/settings/hooks/permissions/use-update-permissions';
import { useSetDefaultUserGroup } from '@/modules/settings/hooks/user-groups/use-set-default-user-group';
import {
  transformFromPermissionRows,
  transformToPermissionRows,
} from '@/modules/settings/mappers/permission.mapper';
import {
  groupFieldPath,
  parseGroupFieldPath,
  PermissionRow,
  UserGroupPermissions,
} from '@/modules/settings/types/permission';
import { UserGroupFormMode } from '@/modules/settings/types/user-group-form-mode';
import {
  PermissionsFormDataValidated,
  permissionsFormSchema,
} from '@/modules/settings/validations/permission-validation';
import { compareFormValues } from '@/utils/form-state.utils';

import { SettingsPageWrapper } from '../../components/settings-page-wrapper';
import { SettingsOutletContext } from '../../types/settings-footer';

/** Row shape as delivered by `form.watch` — RHF deep-partials every level, records included */
type PartialPermissionRow =
  | {
      policy?: OpsiumPolicy;
      groups?: Record<string, boolean | undefined>;
    }
  | undefined;

/** Drives the shared create / rename / duplicate modal; `group` is null in create mode */
interface GroupFormModalState {
  mode: UserGroupFormMode;
  group: UserGroupPermissions | null;
}

const findPolicyIndex = (permissions: PartialPermissionRow[], policy: OpsiumPolicy): number => {
  return permissions.findIndex(permission => permission?.policy === policy);
};

const enableRequiredPolicyForGroup = (
  form: UseFormReturn<PermissionsFormDataValidated>,
  permissions: PartialPermissionRow[],
  policy: OpsiumPolicy,
  groupKey: string
): void => {
  const requiredPolicy = POLICY_DEPENDENCIES[policy];
  if (!requiredPolicy) return;

  const requiredPolicyIndex = findPolicyIndex(permissions, requiredPolicy);
  if (requiredPolicyIndex !== -1 && !permissions[requiredPolicyIndex]?.groups?.[groupKey]) {
    form.setValue(groupFieldPath(requiredPolicyIndex, groupKey), true, { shouldDirty: true });
  }
};

const disableDependentPoliciesForGroup = (
  form: UseFormReturn<PermissionsFormDataValidated>,
  permissions: PartialPermissionRow[],
  policy: OpsiumPolicy,
  groupKey: string
): void => {
  const dependentPolicies = POLICY_DEPENDENTS[policy] ?? [];

  dependentPolicies.forEach(dependentPolicy => {
    const dependentPolicyIndex = findPolicyIndex(permissions, dependentPolicy);
    if (dependentPolicyIndex !== -1 && permissions[dependentPolicyIndex]?.groups?.[groupKey]) {
      form.setValue(groupFieldPath(dependentPolicyIndex, groupKey), false, { shouldDirty: true });
    }
  });
};

export const PermissionsPage = () => {
  const { t } = useTranslation('authenticated.settings');
  const { setFooterState } = useOutletContext<SettingsOutletContext>();
  const { showTimeOffs, showOverheads } = useFeatureFlags();

  const { data: userGroupPermissions, isLoading, error, refetch } = usePermissions();
  const updatePermissionsMutation = useUpdatePermissions();

  const [groupFormModal, setGroupFormModal] = useState<GroupFormModalState | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<UserGroupPermissions | null>(null);

  // Track initial data to compare against
  const initialDataRef = useRef<PermissionRow[]>([]);
  const userGroupPermissionsRef = useRef<UserGroupPermissions[]>([]);

  // Policies hidden from the table because their feature flag is off — still sent to the backend as-is
  const hiddenPolicies = useMemo(
    () => [
      ...(showTimeOffs ? [] : TIME_OFF_OPSIUM_POLICIES),
      ...(showOverheads ? [] : OVERHEADS_OPSIUM_POLICIES),
    ],
    [showTimeOffs, showOverheads]
  );

  // Helper function to map user group permissions to form data
  const mapToFormData = useCallback(
    (ugPermissions: UserGroupPermissions[] | undefined): PermissionRow[] => {
      return ugPermissions ? transformToPermissionRows(ugPermissions, hiddenPolicies) : [];
    },
    [hiddenPolicies]
  );

  // Initialize form default values
  const defaultValues = useMemo(() => {
    return { permissions: mapToFormData(userGroupPermissions) };
  }, [userGroupPermissions, mapToFormData]);

  const form = useForm<PermissionsFormDataValidated>({
    resolver: zodResolver(permissionsFormSchema),
    mode: 'onChange',
    defaultValues,
  });

  // Hook to guard against navigation when there are unsaved changes
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: form.formState.isDirty,
    onDiscard: () => form.reset(),
  });

  // Store initial data when permissions load
  useEffect(() => {
    if (userGroupPermissions) {
      const formData = mapToFormData(userGroupPermissions);
      initialDataRef.current = formData;
      userGroupPermissionsRef.current = userGroupPermissions;
      form.reset({ permissions: formData });
    }
  }, [userGroupPermissions, form, mapToFormData]);

  const handleSave = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();

    // Transform permission rows back to user group permissions
    const updatedUserGroupPermissions = transformFromPermissionRows(
      formData.permissions,
      userGroupPermissionsRef.current,
      hiddenPolicies
    );

    try {
      // The mutation invalidates the matrix; the awaited call resolves after the refetch settles
      await updatePermissionsMutation.mutateAsync(updatedUserGroupPermissions);
      displayToastSuccess(t('pages.permissions.saveSuccess'));
    } catch {
      // Error handled by mutation
    }
  }, [form, updatePermissionsMutation, t, hiddenPolicies]);

  const handleResetToDefaults = useCallback(() => {
    form.reset({ permissions: initialDataRef.current });
  }, [form]);

  // Compare current state with initial using utility
  const hasActualChanges = useCallback(() => {
    const currentValues = form.getValues().permissions;
    const initialValues = initialDataRef.current;
    return compareFormValues(currentValues, initialValues);
  }, [form]);

  /**
   * Group create/rename/delete save immediately, and the refreshed matrix that comes back
   * resets the checkbox form. Gate them behind the unsaved-changes guard so the user is asked
   * to discard first rather than silently losing unsaved ticks.
   */
  // Depends on `checkUnsavedChanges` (stable) rather than the guard object (new every render),
  // so the group action handlers stay stable and the column defs are not rebuilt each render.
  const { checkUnsavedChanges } = unsavedChangesGuard;
  const runWhenClean = useCallback(
    (action: () => void) => {
      if (checkUnsavedChanges(action)) action();
    },
    [checkUnsavedChanges]
  );

  const handleAddGroup = useCallback(
    () => runWhenClean(() => setGroupFormModal({ mode: UserGroupFormMode.Create, group: null })),
    [runWhenClean]
  );

  const handleRenameGroup = useCallback(
    (group: UserGroupPermissions) =>
      runWhenClean(() => setGroupFormModal({ mode: UserGroupFormMode.Rename, group })),
    [runWhenClean]
  );

  const handleDuplicateGroup = useCallback(
    (group: UserGroupPermissions) =>
      runWhenClean(() => setGroupFormModal({ mode: UserGroupFormMode.Duplicate, group })),
    [runWhenClean]
  );

  const handleDeleteGroup = useCallback(
    (group: UserGroupPermissions) => runWhenClean(() => setGroupToDelete(group)),
    [runWhenClean]
  );

  // `mutate` is destructured because the mutation object gets a new identity every render, which
  // would make this handler unstable and rebuild every column def.
  const { mutate: setDefaultUserGroup } = useSetDefaultUserGroup();

  const handleSetDefaultGroup = useCallback(
    (group: UserGroupPermissions) =>
      runWhenClean(() => setDefaultUserGroup({ id: group.id, name: group.name })),
    [runWhenClean, setDefaultUserGroup]
  );

  // Update footer state
  useEffect(() => {
    // Hide footer if there's an error or loading
    if (error || isLoading) {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
      return;
    }

    const hasChanges = hasActualChanges();
    const isValid = form.formState.isValid;

    setFooterState({
      isVisible: true,
      isEnabled: hasChanges && isValid,
      isLoading: updatePermissionsMutation.isPending,
      onSave: handleSave,
      leftAction: hasChanges ? (
        <Button type="button" variant="outline" onClick={handleResetToDefaults}>
          {t('pages.permissions.resetToDefaults')}
        </Button>
      ) : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    error,
    isLoading,
    form.formState.isValid,
    form.formState.isDirty,
    updatePermissionsMutation.isPending,
    hasActualChanges,
  ]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [setFooterState]);

  // Use ref to avoid unnecessary re-renders but stay reactive to data loading
  const [tableData, setTableData] = React.useState<PermissionRow[]>([]);

  // Update table data when form loads, but not on every field change
  React.useEffect(() => {
    const currentData = form.getValues().permissions;
    if (currentData && currentData.length > 0) {
      setTableData(currentData);
    }
  }, [userGroupPermissions, form]);

  // Stable index lookup for dependency resolution — recomputed only when data loads
  const policyIndexMap = useMemo<Partial<Record<OpsiumPolicy, number>>>(
    () =>
      tableData.reduce(
        (acc, row, idx) => ({ ...acc, [row.policy]: idx }),
        {} as Partial<Record<OpsiumPolicy, number>>
      ),
    [tableData]
  );

  // Cascade logic: auto-check View when a dependent is checked; auto-uncheck dependents when View is unchecked
  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (type !== 'change' || !name) return;

      const parsedField = parseGroupFieldPath(name);
      if (!parsedField) return;

      const permissions = value.permissions;
      if (!permissions) return;

      const changedRow = permissions[parsedField.rowIndex];
      if (!changedRow) return;

      const policy = changedRow.policy as OpsiumPolicy;
      const isChecked = Boolean(changedRow.groups?.[parsedField.groupKey]);

      if (isChecked) {
        enableRequiredPolicyForGroup(form, permissions, policy, parsedField.groupKey);
        return;
      }

      disableDependentPoliciesForGroup(form, permissions, policy, parsedField.groupKey);
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const userGroups = useMemo(() => userGroupPermissions ?? [], [userGroupPermissions]);

  const columns = useMemo<ColumnDef<PermissionRow>[]>(
    () =>
      getPermissionsColumns({
        userGroups,
        policyIndexMap,
        onRenameGroup: handleRenameGroup,
        onDuplicateGroup: handleDuplicateGroup,
        onDeleteGroup: handleDeleteGroup,
        onSetDefaultGroup: handleSetDefaultGroup,
        t,
      }),
    [
      userGroups,
      policyIndexMap,
      handleRenameGroup,
      handleDuplicateGroup,
      handleDeleteGroup,
      handleSetDefaultGroup,
      t,
    ]
  );

  // Group permissions by category for DataTable grouping
  const groupedPermissions = useMemo(() => {
    if (!tableData.length) return [];

    const groups: Array<{ category: string; items: PermissionRow[] }> = [];
    let currentCategory: string | null = null;
    let currentGroup: PermissionRow[] = [];

    tableData.forEach(permission => {
      const category = POLICY_CATEGORIES[permission.policy];

      if (category !== currentCategory) {
        if (currentGroup.length > 0) {
          groups.push({ category: currentCategory!, items: currentGroup });
        }
        currentCategory = category;
        currentGroup = [permission];
      } else {
        currentGroup.push(permission);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ category: currentCategory!, items: currentGroup });
    }

    return groups;
  }, [tableData]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    // Keeps the permission name readable once enough groups exist to scroll horizontally
    initialState: { columnPinning: { left: [PERMISSION_POLICY_COLUMN_ID] } },
  } as TableOptions<PermissionRow>);

  const unsavedChangesGuardProps = useMemo(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: unsavedChangesGuard.handleModalCancel,
    }),
    [
      unsavedChangesGuard.isModalOpen,
      unsavedChangesGuard.handleModalDiscard,
      unsavedChangesGuard.handleModalCancel,
    ]
  );

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error}
      onRetry={refetch}
      unsavedChangesGuard={unsavedChangesGuardProps}
    >
      <div className="absolute inset-0 flex flex-col overflow-hidden py-6">
        <Card
          className="flex min-h-0 w-full flex-1 flex-col overflow-hidden p-6"
          style={{ maxWidth: '1240px' }}
        >
          {/* Card Header - Fixed */}
          <div className="mb-6 flex shrink-0 items-start justify-between gap-4">
            <div>
              <h2 className="mb-2 text-lg font-semibold leading-7">
                {t('pages.permissions.title')}
              </h2>
              <p className="text-xs text-muted-foreground">{t('pages.permissions.description')}</p>
            </div>
            <Button type="button" variant="outline" className="gap-2" onClick={handleAddGroup}>
              <CirclePlus className="h-4 w-4" />
              {t('pages.permissions.addGroup')}
            </Button>
          </div>

          {/* Table with Scrollable Body */}
          <Form {...form}>
            <form className="flex min-h-0 flex-1 flex-col">
              <PermissionsTable
                table={table}
                stickyHeader
                groupedPermissions={groupedPermissions}
              />
            </form>
          </Form>
        </Card>
      </div>

      <UserGroupFormModal
        open={groupFormModal !== null}
        onClose={() => setGroupFormModal(null)}
        mode={groupFormModal?.mode ?? UserGroupFormMode.Create}
        group={groupFormModal?.group ?? null}
        existingGroups={userGroups}
      />

      <DeleteUserGroupModal
        group={groupToDelete}
        allGroups={userGroups}
        open={groupToDelete !== null}
        onClose={() => setGroupToDelete(null)}
        onSuccess={() => setGroupToDelete(null)}
      />
    </SettingsPageWrapper>
  );
};
